import { PAUSED_UPLOAD_TTL_HOURS } from "@/components/UploadProgress/UploadProgressLoadingCard";
import { UploadBasicsArticle } from "./articles/UploadBasicsArticle";
import { UploadStatusesArticle } from "./articles/UploadStatusesArticle";
import { CancelVsContinueArticle } from "./articles/CancelVsContinueArticle";
import { MoveToAnotherDeviceArticle } from "./articles/MoveToAnotherDeviceArticle";
import { ResetPasswordArticle } from "./articles/ResetPasswordArticle";

/**
 * Tutorial registry — the single list of tours + articles the app knows about.
 *
 * How to add a tutorial
 * ---------------------
 * 1. Add an entry below. `id` is the persistence key — never rename it.
 * 2. `mode`:
 *      "playground" — the tour runs inside the tutorial popup over the sandbox
 *                     (steps may carry `playground: {...}` to set the sandbox
 *                     state and `advanceWhen` to wait for the user to act);
 *      "app"        — the tour runs over the real screens (steps use `route`);
 *      "article"    — no tour at all (steps: []): the popup shows just the
 *                     article with a "Mark as read" button (read = COMPLETED,
 *                     closing without it = SKIPPED, same as leaving a tour).
 * 3. Steps target `data-tour="…"` attributes (never data-testid, e2e owns those).
 *    Every target must exist somewhere under src/ — registry.test.js greps for it.
 * 4. `promptOnRelease: true` makes the post-login prompt offer it to everyone
 *    who hasn't completed/skipped this `version`.
 * 5. Changed a tour materially? Bump `version` — users who did v1 get prompted again.
 * 6. `addedOn` (YYYY-MM-DD): the overview lists newest first, so a fresh
 *    tutorial lands on top. Today's order, top → bottom: reset password →
 *    other device → cancel → statuses → basics. The post-login prompt uses the order below
 *    (basics first) — that one is about learning, not recency.
 *
 * Step fields: target, title, description (HTML allowed: <strong>, <em>),
 * route?, playground? ({ reset?: true, ...patch }), advanceWhen? (selector),
 * optional?, side?, align?
 */

const TTL = PAUSED_UPLOAD_TTL_HOURS;
const cardIn = (state) => `[data-tour="upload-card"][data-demo-state="${state}"]`;

export const TUTORIALS = [
  {
    id: "upload-basics",
    version: 1,
    addedOn: "2026-08-17",
    mode: "app",
    promptOnRelease: true,
    title: "How uploading works",
    summary: "Open the form, add files, fill in the details the newsroom needs, submit. On the real screen.",
    estimatedMinutes: 3,
    previewState: "uploading",
    route: "/uploads",
    // When the tour ends: practice values are removed (MetadataForm) and, on
    // Done, the upload form closes (UploadClips) and we return to /tutorials.
    endEvent: "clipn:tutorial-upload-tour-ended",
    endRoute: "/tutorials",
    Article: UploadBasicsArticle,
    steps: [
      {
        target: '[data-tour="upload-open-button"]',
        route: "/uploads",
        title: "Open the upload form",
        description: "Click <strong>Upload clips</strong>. This is where every package starts.",
        advanceWhen: '[data-tour="dropzone"]',
        noAutoAct: true,
        side: "bottom",
        align: "end",
      },
      {
        target: '[data-tour="dropzone"]',
        title: "Add your files",
        description:
          "Drop your clips here or click <strong>browse files</strong> and pick them. Files are cut into small <strong>chunks</strong>; each chunk is confirmed by the server before the next one goes, so a dropped connection only ever costs the chunk in flight.",
        advanceWhen: '[data-tour="upload-file-list"]',
        noAutoAct: true,
        side: "right",
      },
      {
        target: '[data-tour="upload-continue-button"]',
        title: "Continue to the details",
        description: "Your files are listed with the total size. Press <strong>Continue</strong>.",
        advanceWhen: '[data-tour="metadata-form"]',
        noAutoAct: true,
        side: "top",
      },
      {
        target: '[data-tour="metadata-form"]',
        title: "Fill in the details the newsroom needs",
        description:
          "Fields marked <strong>*</strong> are required: <strong>Package name</strong> (how the desk will find it), <strong>Description</strong> (what's on the footage), <strong>Country</strong>, <strong>State/Region</strong>, <strong>City</strong> and the <strong>Shot date</strong>. <em>Notes</em> is optional. Type the real details — <strong>Submit</strong> lights up when every required field is filled. (<em>Do it for me</em> enters clearly-marked practice values; replace them before a real submit.)",
        advanceWhen: '[data-tour="upload-start-button"]:not([disabled])',
        event: "clipn:tutorial-fill-metadata",
        side: "top",
      },
      {
        target: '[data-tour="metadata-embargo"]',
        title: "Embargo date & time (optional)",
        description:
          "Switch this on if the footage must <strong>not be published before</strong> a certain date and time. The date travels with the package to the newsroom system, so editors see the restriction. Leave it off for normal news.",
        side: "top",
      },
      {
        target: '[data-tour="metadata-notify"]',
        title: "Notify your assignment editor (optional)",
        description:
          "Switch this on and add the editor's e-mail: they get a copy of the <strong>“upload delivered”</strong> (or “upload failed”) e-mail for this package, so they know the footage is in without you calling. You can add more than one address.",
        side: "top",
      },
      {
        target: '[data-tour="upload-start-button"]',
        noInteraction: true,
        title: "Submit — when it's real",
        description:
          "When you have real footage and real details, <strong>Submit</strong> starts the upload; from there the uploader looks after itself — watch the card, don't cancel when it says <em>retrying</em>. <strong>The tour stops here and sends nothing.</strong> Press <strong>Done</strong> to close this form and go back to Tutorials (practice values are cleared) — or press ✕ to keep the form open and submit your real package yourself.",
        side: "top",
      },
    ],
  },
  {
    id: "reset-password",
    version: 1,
    addedOn: "2026-08-20",
    mode: "article",
    promptOnRelease: true,
    title: "Reset your password",
    summary:
      "Forgot Password?, then check your inbox AND the Spam folder — that's where the reset e-mail usually hides.",
    estimatedMinutes: 2,
    Article: ResetPasswordArticle,
    steps: [],
  },
  {
    id: "upload-statuses",
    version: 1,
    addedOn: "2026-08-18",
    mode: "playground",
    scope: '[data-tour="playground"]',
    promptOnRelease: true,
    title: "What each status means",
    summary: "Healthy, slow, reconnecting, paused, after a reload, finalizing — see each one and learn what (not) to do.",
    estimatedMinutes: 4,
    previewState: "stalled",
    Article: UploadStatusesArticle,
    steps: [
      {
        target: '[data-tour="upload-speed"]',
        playground: { reset: true },
        title: "Healthy",
        description:
          "Everything in this tour is a <strong>simulation</strong> on a copy of the upload card — no real upload and no real connection are touched. This is a healthy upload: bytes flowing, speed shown, files ticking from Queued to Done. Leave the tab open — that's the whole job.",
        side: "right",
      },
      {
        target: '[data-tour="pg-network-slow"]',
        side: "top",
        title: "Try a slow connection",
        description: "Click <strong>Slow</strong> to see what a weak signal looks like.",
        advanceWhen: cardIn("slow"),
        action: { type: "network", network: "slow" },
      },
      {
        target: '[data-tour="upload-card"]',
        title: "Slow — not a problem",
        description:
          "The uploader adapts to whatever bandwidth you have. The uploaded counter switches to <strong>KB</strong> so you can see it moving even at a few hundred KB/s.",
        side: "right",
      },
      {
        target: '[data-tour="pg-network-unstable"]',
        side: "top",
        title: "Now make it unstable",
        description: "Click <strong>Unstable</strong> — the connection keeps dropping.",
        advanceWhen: cardIn("stalled"),
        action: { type: "network", network: "unstable" },
      },
      {
        target: '[data-tour="upload-stall-banner"]',
        title: "Stalled — retrying automatically",
        description:
          "No bytes for a while. The uploader has already noticed and is retrying on its own. It continues from the last confirmed chunk. <strong>Don't cancel</strong> — that throws away what's already uploaded.",
        side: "right",
      },
      {
        target: '[data-tour="pg-network-offline"]',
        side: "top",
        title: "Go fully offline",
        description: "Click <strong>Offline</strong> — we only pretend the connection is gone; your real Wi-Fi is untouched.",
        advanceWhen: cardIn("retrying"),
        action: { type: "network", network: "offline" },
      },
      {
        target: '[data-tour="upload-stall-banner"]',
        side: "right",
        title: "Reconnecting…",
        description:
          "Offline, the card says <em>You appear to be offline</em> and <em>Reconnecting…</em> — the uploader keeps trying by itself and will continue from the last confirmed chunk. Nothing for you to press.",
      },
      {
        target: '[data-tour="pg-event-long-outage"]',
        side: "top",
        title: "…and stay offline a while",
        description:
          "The card says <em>Reconnecting…</em> and keeps trying. If the outage lasts, the app <strong>pauses</strong> the upload to protect it. Click <strong>Stay offline a while</strong> to simulate a long outage (nothing real is affected).",
        advanceWhen: cardIn("autoPaused"),
        action: { type: "longOutage" },
      },
      {
        target: '[data-tour="upload-progress"]',
        // Above the card: this step names the amber bar AND the ▶ icon to its
        // left, so the popover must not sit over either of them.
        side: "right",
        title: "This is what paused looks like",
        description:
          "The bar turns <strong>amber</strong> and stops growing, the time-left and speed rows disappear, and the icon on the left becomes a <strong>▶ play</strong> button. The card says <em>Upload paused.</em> and how long it is kept — every byte already sent is safe on the server.",
      },
      {
        target: '[data-tour="upload-state-control"]',
        // Above the card: to the right/below it would sit on "Upload paused."
        side: "top",
        title: "The play button is live",
        description:
          "You never have to wait: while an upload is paused the icon is a working <strong>▶ play</strong> button — press it whenever you have signal again. The app also keeps trying by itself, which is what we'll do next.",
      },
      {
        target: '[data-tour="pg-network-good"]',
        side: "top",
        title: "Auto-paused — it resumes itself",
        description:
          "This is the auto-paused card. When the network comes back the app resumes on its own (up to 3 tries); if that fails you get one small <em>Resume?</em> popup. Click <strong>Good</strong> to bring the network back.",
        advanceWhen: cardIn("uploading"),
        action: { type: "network", network: "good" },
      },
      {
        target: '[data-tour="upload-state-control"]',
        title: "Pause it yourself",
        description: "The icon on the left is the pause/play button. Click it to <strong>pause</strong>.",
        advanceWhen: cardIn("pausedByUser"),
        action: { type: "pause" },
        side: "right",
      },
      {
        target: '[data-tour="upload-expires"]',
        title: `Paused uploads wait ${TTL} hours`,
        description: `The card shows the countdown. Move to better Wi-Fi, come back, press play — already-sent chunks aren't re-sent.`,
        side: "right",
      },
      {
        target: '[data-tour="upload-state-control"]',
        side: "top",
        title: "Now press play to continue",
        description:
          "Back on better Wi-Fi you press <strong>▶</strong> and the upload carries on from the last confirmed chunk — nothing already sent is uploaded again.",
        advanceWhen: cardIn("uploading"),
        action: { type: "resume" },
      },
      {
        target: '[data-tour="pg-event-reload"]',
        side: "top",
        title: "What if the page reloads?",
        description: "Click <strong>Reload page</strong> — this is also what a closed tab or a crashed browser looks like.",
        advanceWhen: cardIn("interrupted"),
        action: { type: "reload" },
      },
      {
        target: '[data-tour="upload-repick"]',
        action: { type: "repick" },
        title: "Select the same files once more",
        description:
          "Browsers can't keep your files across a reload, so the card asks for them again. Already-uploaded chunks are skipped. Click <strong>Select files</strong>.",
        advanceWhen: cardIn("uploading"),
        // To the side: the "Expires in…" and "interrupted" lines sit right
        // above these buttons and must stay readable.
        side: "right",
      },
      {
        target: '[data-tour="pg-event-finish"]',
        side: "top",
        title: "Let it finish",
        description: "Click <strong>Finish upload</strong> — all the bytes are in and the server takes over.",
        advanceWhen: cardIn("finalizing"),
        action: { type: "finish" },
      },
      {
        target: '[data-tour="upload-card"]',
        side: "right",
        title: "Finalizing on server",
        description:
          "<em>Files uploaded. Finishing package preparation…</em> — the server is assembling and checking the files. This can take a minute on a big package, and you can close the tab: it finishes without you.",
      },
      {
        target: '[data-tour="pg-event-finish"]',
        side: "top",
        title: "…and finished",
        description: "Click <strong>Finish upload</strong> once more to see the delivered card.",
        advanceWhen: cardIn("completed"),
        action: { type: "finish" },
      },
      {
        target: '[data-tour="upload-card"]',
        title: "Done",
        description: "Delivered. The package shows up under Uploads. That's every state you'll ever see.",
        side: "right",
      },
    ],
  },
  {
    id: "cancel-vs-continue",
    version: 1,
    addedOn: "2026-08-19",
    mode: "playground",
    scope: '[data-tour="playground"]',
    promptOnRelease: true,
    title: "When to use Cancel",
    summary: "Cancel deletes the package. Slow or offline is not a reason — here's what to do instead.",
    estimatedMinutes: 1,
    previewState: "canceled",
    Article: CancelVsContinueArticle,
    steps: [
      {
        target: '[data-tour="upload-stall-banner"]',
        playground: { reset: true, network: "unstable" },
        title: "Looks stuck? It's retrying",
        description:
          "This is a simulated card — nothing real is uploading. It looks alarming but is healthy. The uploader saw the silence and is retrying on its own. Cancelling now would <strong>throw away everything already uploaded</strong> — and you'd restart on the same connection.",
        side: "left",
      },
      {
        target: '[data-tour="upload-state-control"]',
        title: "Need to move? Pause",
        description: `Closing the laptop, changing location, driving back? Press the <strong>pause icon</strong> (or just close the tab). You have ${TTL} hours to come back and press play. Try it.`,
        advanceWhen: cardIn("pausedByUser"),
        action: { type: "pause" },
        side: "right",
      },
      {
        target: '[data-tour="upload-cancel"]',
        title: "Cancel = don't deliver this package",
        description:
          "Cancel asks for a short reason and <strong>deletes the package on the server</strong>. Use it for wrong files or a package that must not go out — never to fix a slow connection. Go on, press it here to see.",
        advanceWhen: cardIn("canceled"),
        action: { type: "cancel" },
        side: "top",
      },
      {
        target: '[data-tour="upload-card"]',
        title: "Gone",
        description: "Everything already uploaded was deleted. In real life you'd start from zero. Slow → wait. Offline → wait or pause. Another device → take over. Wrong files → Cancel.",
        side: "left",
      },
    ],
  },

  {
    id: "move-to-another-device",
    version: 1,
    addedOn: "2026-08-20",
    mode: "playground",
    scope: '[data-tour="playground"]',
    promptOnRelease: true,
    title: "Move an upload to another device",
    summary: "Started on the laptop, finish at the desk: take the package over on any signed-in device — nothing is re-uploaded.",
    estimatedMinutes: 2,
    previewState: "anotherDevice",
    Article: MoveToAnotherDeviceArticle,
    steps: [
      {
        target: '[data-tour="upload-state-control"]',
        playground: { reset: true },
        title: "You're uploading on device A",
        description: "A simulation — no real upload is touched. Say the hotel Wi-Fi is hopeless and you'd rather finish at the newsroom desk. First, <strong>pause</strong> here (or just close the tab).",
        advanceWhen: cardIn("pausedByUser"),
        action: { type: "pause" },
        side: "right",
      },
      {
        target: '[data-tour="pg-event-other-device"]',
        title: "Now sign in on device B",
        description: "Click <strong>Open on another device</strong> to see what the second computer shows.",
        advanceWhen: cardIn("anotherDevice"),
        action: { type: "otherDevice" },
        side: "bottom",
      },
      {
        target: '[data-tour="upload-take-over"]',
        title: "Take over here",
        description:
          "The package is marked <em>another device</em>. An account runs one upload at a time, so this device waits until you press <strong>Take over here</strong>. Press it.",
        advanceWhen: cardIn("interrupted"),
        action: { type: "takeOver" },
        side: "top",
      },
      {
        target: '[data-tour="upload-repick"]',
        action: { type: "repick" },
        title: "Select the same files on this device",
        description:
          "Copy the footage over first (card reader, drive, share). Then <strong>Select files</strong> — the uploader compares them with what the server has and sends only the missing chunks.",
        advanceWhen: cardIn("uploading"),
        side: "top",
      },
      {
        target: '[data-tour="upload-card"]',
        title: "Continuing, not restarting",
        description: `Nothing was re-uploaded. You have ${TTL} hours from the pause to do this. And remember: never Cancel to "free" an upload — that deletes it.`,
        side: "left",
      },
    ],
  },
];

export const getTutorial = (id) => TUTORIALS.find((t) => t.id === id) || null;

/** Overview order: newest `addedOn` first, registry order for ties. */
export const TUTORIALS_NEWEST_FIRST = TUTORIALS.map((t, i) => ({ t, i }))
  .sort((a, b) => String(b.t.addedOn || "").localeCompare(String(a.t.addedOn || "")) || a.i - b.i)
  .map(({ t }) => t);
